import ImportBatchPanel from "./ImportBatchPanel";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Input, Space, Typography } from "antd";
import { getInteractionTask, confirmInteractionAction, reviseInteractionAction, cancelInteractionTask } from "./api";
import { proposalChanges, sameChanges, taskMatchesScope } from "./interactionValues.mjs";
import { isRecordEditing, RECORD_EDIT_EVENT, RECORD_SAVED_EVENT } from "./recordEditState.mjs";
import { emitEvent, EventName } from "@/hooks/useEventBus";
import { buildFacilityPath } from "@/utils/route-context";

const display = (value) => value == null || value === "" ? "Not set" : String(value);

function ActionPreview({ action, task, companyId, facilityId, disabled, onTask }) {
  const [values, setValues] = useState(action.changes);
  const [selected, setSelected] = useState(Object.keys(action.changes));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [conflict, setConflict] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    const read = () => setEditing(isRecordEditing({ companyId, facilityId, resource: action.resource, recordId: action.record_id }));
    read();
    window.addEventListener(RECORD_EDIT_EVENT, read);
    return () => window.removeEventListener(RECORD_EDIT_EVENT, read);
  }, [companyId, facilityId, action.resource, action.record_id]);
  const changes = proposalChanges(values, selected);
  const dirty = !sameChanges(changes, action.changes);
  const save = async (revise) => {
    if (busy || disabled || editing) return;
    setBusy(true);
    setError("");
    try {
      const args = { companyId, facilityId, actionId: action.id, revision: action.revision, taskRevision: task.revision };
      const result = revise ? await reviseInteractionAction({ ...args, changes }) : await confirmInteractionAction(args);
      if (!mounted.current) return;
      onTask(result.task);
      if (result.receipt?.verified) {
        emitEvent(EventName.REFRESH_WORKORDER_LIST);
        window.dispatchEvent(new CustomEvent(RECORD_SAVED_EVENT, { detail: { ...result.receipt, companyId, facilityId } }));
      }
    } catch (failure) {
      if (mounted.current) {
        setError(failure.message || "The proposal could not be saved.");
        setConflict(failure.status === 409);
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  const locked = disabled || busy || editing;
  return <div aria-label="Work order change preview" className="facility-ai-action-preview">
    <Typography.Text strong>{action.operation === "create" ? "New work order" : `Update work order #${action.record_id}`}</Typography.Text>
    <p>{action.summary}</p>
    {Object.keys(action.changes).map((field) => <div key={field} className="facility-ai-change-field">
      <Checkbox disabled={locked} checked={selected.includes(field)} onChange={(event) => setSelected((old) => event.target.checked ? [...old, field] : old.filter((item) => item !== field))}>
        {action.labels[field] || field}
      </Checkbox>
      <div className="facility-ai-change-before">Current: {display(action.before[field])}</div>
      <Input.TextArea aria-label={`Proposed ${action.labels[field] || field}`} value={values[field] ?? ""} disabled={locked || !selected.includes(field)} autoSize={{ minRows: 1, maxRows: 5 }} onChange={(event) => setValues((old) => ({ ...old, [field]: event.target.value }))} />
    </div>)}
    {editing && <Alert type="info" message="Finish or cancel your open work-order edits before saving this proposal." />}
    {error && <Alert type="error" message={error} />}
    <Space wrap style={{ marginTop: 12 }}>
      {(dirty || conflict) && <Button disabled={locked || !Object.keys(changes).length} loading={busy} onClick={() => save(true)}>{conflict ? "Refresh preview" : "Update preview"}</Button>}
      <Button type="primary" disabled={locked || dirty || conflict} loading={busy} onClick={() => save(false)}>Confirm and save</Button>
    </Space>
    <p className="facility-ai-task-note">Nothing is saved until you confirm. Changing fields requires an updated preview.</p>
  </div>;
}

export default function InteractionTaskPanel({ companyId, facilityId, conversationId, isStreaming, onReply, onTaskChange }) {
  const [task, setTask] = useState(null);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const scopeRef = useRef("");
  scopeRef.current = `${companyId}:${facilityId}:${conversationId}`;
  const acceptTask = useCallback((next) => {
    if (taskMatchesScope(next, companyId, facilityId)) {
      setTask(next);
      setAnswers({});
    } else setTask(null);
  }, [companyId, facilityId]);
  useEffect(() => {
    setTask(null);
    setAnswers({});
    setError("");
  }, [companyId, facilityId, conversationId]);
  useEffect(() => {
    if (!conversationId || isStreaming) return undefined;
    const controller = new AbortController();
    getInteractionTask({ companyId, facilityId, conversationId, signal: controller.signal })
      .then((result) => { if (!controller.signal.aborted) { acceptTask(result.task); setError(""); } })
      .catch((failure) => { if (!controller.signal.aborted && failure.status !== 503) setError(failure.message); });
    return () => controller.abort();
  }, [companyId, facilityId, conversationId, isStreaming, acceptTask]);
  useEffect(() => {
    if (task) onTaskChange?.();
  }, [task, onTaskChange]);
  const cancel = async () => {
    const scope = scopeRef.current;
    setBusy(true);
    try {
      const next = await cancelInteractionTask({ companyId, facilityId, conversationId });
      if (scope === scopeRef.current) acceptTask(next);
    } catch (failure) { if (scope === scopeRef.current) setError(failure.message); }
    finally { if (scope === scopeRef.current) setBusy(false); }
  };
  if (!task) return error ? <Alert type="error" message={error} /> : null;
  if (task.status === "restricted") return <Alert type="info" message="Your access to this task’s source records has changed. Start a new conversation with your current access." />;
  const pending = task.actions.find((action) => action.status === "prepared");
  const saved = task.status === "complete" && task.actions.find((action) => action.id === task.completed_action_id && action.status === "completed" && action.receipt?.verified);
  const questions = task.status === "awaiting_input" ? task.questions : [];
  const reply = () => {
    const filled = Object.fromEntries(Object.entries(answers).filter(([, value]) => value.trim()));
    if (!Object.keys(filled).length || isStreaming) return;
    const text = questions.filter((question) => filled[question.key]).map((question) => `${question.question}\n${filled[question.key]}`).join("\n\n");
    onReply(text, { task_id: task.id, revision: task.revision, answers: filled });
  };
  const canApply = task.target?.resource === "workorders" && task.status === "complete" && !saved && !pending;
  if (!task.import_batch && !questions.length && !pending && !saved && !canApply && !error) return null;
  return <section className="facility-ai-interaction-task" aria-label="Current AI task">
    {error && <Alert type="error" message={error} />}
    {questions.length > 0 && <div>
      <Typography.Text strong>Waiting for your information</Typography.Text>
      <p className="facility-ai-task-note">Answer below or reply by typing or speaking in the conversation. The same task will continue.</p>
      {questions.map((question) => <div key={question.key} className="facility-ai-followup-question">
        <label htmlFor={`ai-question-${task.id}-${question.key}`}>{question.question}</label>
        {question.reason && <p className="facility-ai-task-note">{question.reason}</p>}
        <Space wrap>{question.options.map((option) => <Button key={option} size="small" disabled={isStreaming} onClick={() => setAnswers((old) => ({ ...old, [question.key]: option }))}>{option}</Button>)}</Space>
        <Input.TextArea id={`ai-question-${task.id}-${question.key}`} value={answers[question.key] || ""} disabled={isStreaming} rows={2} onChange={(event) => setAnswers((old) => ({ ...old, [question.key]: event.target.value }))} />
      </div>)}
      <Button type="primary" disabled={isStreaming || !Object.values(answers).some((value) => value.trim())} onClick={reply}>Continue task</Button>
    </div>}
    {task.import_batch && task.status !== "cancelled" && <ImportBatchPanel key={`${task.id}:${task.revision}`} task={task} action={pending?.resource === "imports" ? pending : null} companyId={companyId} facilityId={facilityId} disabled={isStreaming || busy} onTask={acceptTask} onReply={onReply} />}
    {pending && pending.resource !== "imports" && <ActionPreview key={`${task.id}:${pending.id}:${pending.revision}`} action={pending} task={task} companyId={companyId} facilityId={facilityId} disabled={isStreaming || busy} onTask={acceptTask} />}
    {saved && saved.resource !== "imports" && <Alert type="success" message="Saved and verified" description={<a href={buildFacilityPath(`workorder-detail/${saved.receipt.record_id}`, { companyId, facilityId })}>Open saved work order #{saved.receipt.record_id}</a>} />}
    {canApply && <Button disabled={isStreaming} onClick={() => onReply("Prepare an update to this reviewed work order based on your recommendations. Ask me for any missing decisions, then show the proposed changes before saving.")}>Prepare changes from recommendations</Button>}
    {(questions.length > 0 || pending || (task.import_batch && task.status !== "complete" && task.status !== "cancelled")) && <Button type="link" disabled={busy || isStreaming} onClick={cancel}>Cancel task</Button>}
  </section>;
}
